import React, { useEffect, useMemo, useRef } from 'react';
import { LeadForm } from './LeadForm';
import {
  contactFields,
  createApplicationFields,
  createRoleOptions,
  demoFields,
  GENERAL_APPLICATION_VALUE,
} from './leadFormConfigs';

function getModalConfig(modal, jobs) {
  const selectedRole = modal?.role || GENERAL_APPLICATION_VALUE;

  if (modal?.type === 'contact') {
    return {
      formName: 'contact',
      title: 'Contact form',
      intro: 'Share the essentials so the right person can reply quickly.',
      fields: contactFields,
      submitLabel: 'Send message',
    };
  }

  if (modal?.type === 'jobApplication') {
    return {
      formName: 'jobApplication',
      title: 'Application form',
      intro: 'Apply for a named role or send a general application directly from the site.',
      fields: createApplicationFields(selectedRole),
      includeFileUpload: true,
      includeRoleOptions: createRoleOptions(jobs, selectedRole),
      submitLabel: 'Submit application',
    };
  }

  return {
    formName: 'demoRequest',
    title: 'Demo request',
    intro: 'Tell us about your part or process and we\u2019ll tailor the conversation.',
    fields: demoFields,
    submitLabel: 'Request demo',
  };
}

export function CtaFormModal({ modal, onClose, jobs = [], contact }) {
  const closeButtonRef = useRef(null);
  const config = useMemo(() => getModalConfig(modal, jobs), [jobs, modal]);

  useEffect(() => {
    if (!modal) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [modal, onClose]);

  if (!modal) {
    return null;
  }

  const modalKey = `${config.formName}-${modal.role || ''}`;

  return (
    <div
      className='cta-modal-backdrop'
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <section className='cta-modal' role='dialog' aria-modal='true' aria-label={config.title}>
        <button ref={closeButtonRef} className='cta-modal-close' type='button' onClick={onClose} aria-label='Close form'>
          <span aria-hidden='true'>&times;</span>
        </button>
        <LeadForm key={modalKey} {...config} />
        {modal.type === 'contact' && contact ? (
          <div className='cta-modal-contact'>
            <a className='inline-link' href={`mailto:${contact.email}`}>
              {contact.email}
            </a>
          </div>
        ) : null}
      </section>
    </div>
  );
}
